import { useEffect, useRef, useCallback, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useWebRTC, CallType, SignalingMessage } from '../hooks/useWebRTC'
import { WSMessageTypes } from '../hooks/useWebSocket'
import { useSharedWebSocket } from '../contexts/WebSocketContext'

export default function CallPage() {
  const { recipientId } = useParams<{ recipientId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { sendRTCOffer, sendRTCAnswer, sendICECandidate, sendMessage, lastMessage, isConnected } = useSharedWebSocket()

  const callType = (searchParams.get('type') as CallType) || 'audio'
  const isIncoming = searchParams.get('incoming') === 'true'

  const localVideoRef = useRef<HTMLVideoElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)
  const currentCallTypeRef = useRef<CallType>(callType)
  const hasStartedCallRef = useRef(false)

  const [isSpeaker, setIsSpeaker] = useState(false)
  const [isScreenSharing, setIsScreenSharing] = useState(false)

  const handleSignalingMessage = useCallback((message: SignalingMessage) => {
    if (!recipientId) return
    switch (message.type) {
      case 'offer':
        if (message.sdp?.sdp) sendRTCOffer(recipientId, message.sdp.sdp, currentCallTypeRef.current)
        break
      case 'answer':
        if (message.sdp?.sdp) sendRTCAnswer(recipientId, message.sdp.sdp)
        break
      case 'ice-candidate':
        if (message.candidate) sendICECandidate(recipientId, message.candidate)
        break
      case 'end-call':
        sendMessage({ type: 'rtc_hangup', payload: { target_id: recipientId } })
        break
      case 'reject-call':
        sendMessage({ type: 'rtc_reject', payload: { target_id: recipientId } })
        break
    }
  }, [recipientId, sendRTCOffer, sendRTCAnswer, sendICECandidate, sendMessage])

  const { callState, callType: currentCallType, localStream, remoteStream, isMuted, isVideoOff, formattedDuration, error, startCall, answerCall, handleAnswer, handleIceCandidate, endCall, toggleMute, toggleVideo, switchCamera } = useWebRTC(recipientId || '', handleSignalingMessage)

  useEffect(() => { currentCallTypeRef.current = currentCallType }, [currentCallType])

  useEffect(() => {
    if (!recipientId || hasStartedCallRef.current) return
    if (!isIncoming && !isConnected) return
    if (isIncoming) {
      const storedSdp = sessionStorage.getItem('incoming_call_sdp')
      const storedType = sessionStorage.getItem('incoming_call_type') as CallType
      if (storedSdp) {
        hasStartedCallRef.current = true
        answerCall({ type: 'offer', sdp: storedSdp }, storedType || callType)
        sessionStorage.removeItem('incoming_call_sdp')
        sessionStorage.removeItem('incoming_call_type')
      }
    } else {
      hasStartedCallRef.current = true
      startCall(callType)
    }
  }, [recipientId, callType, isIncoming, startCall, answerCall, isConnected])

  useEffect(() => {
    if (!lastMessage) return
    const { type, payload } = lastMessage
    const data = payload?.data as Record<string, unknown> | undefined
    switch (type) {
      case WSMessageTypes.RTC_OFFER:
        if (data?.sdp) answerCall({ type: 'offer', sdp: data.sdp as string }, (data.call_type as CallType) || 'audio')
        break
      case WSMessageTypes.RTC_ANSWER:
        if (data?.sdp) handleAnswer({ type: 'answer', sdp: data.sdp as string })
        break
      case WSMessageTypes.RTC_CANDIDATE:
        if (data) handleIceCandidate(data as RTCIceCandidateInit)
        break
      case 'rtc_hangup': case 'rtc_reject': navigate(-1); break
    }
  }, [lastMessage, handleAnswer, handleIceCandidate, answerCall, navigate])

  useEffect(() => { if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream }, [localStream])
  useEffect(() => { if (remoteVideoRef.current && remoteStream) remoteVideoRef.current.srcObject = remoteStream }, [remoteStream])

  const handleEndCall = () => { endCall(); navigate(-1) }

  const handleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        // Stop screen sharing - revert to camera
        if (localStream) {
          const videoTrack = localStream.getVideoTracks()[0]
          if (videoTrack) videoTrack.stop()
        }
        setIsScreenSharing(false)
      } else {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
        if (localVideoRef.current) localVideoRef.current.srcObject = screenStream
        setIsScreenSharing(true)
        screenStream.getVideoTracks()[0].onended = () => setIsScreenSharing(false)
      }
    } catch { console.log('Screen share cancelled') }
  }

  const getStatusText = () => {
    switch (callState) {
      case 'calling': return '撥號中...'
      case 'ringing': return '響鈴中...'
      case 'connected': return formattedDuration
      case 'failed': return error || '通話失敗'
      case 'ended': return '通話結束'
      default: return ''
    }
  }

  const isVideoCall = currentCallType === 'video'

  return (
    <div className={`min-h-screen flex flex-col ${isVideoCall ? 'bg-black' : 'bg-gradient-to-b from-primary to-primary-dark'}`}>
      {/* Video views */}
      {isVideoCall && (
        <>
          <video ref={remoteVideoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute top-20 right-4 w-32 h-44 rounded-xl overflow-hidden shadow-lg z-10 border-2 border-white/20">
            <video ref={localVideoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`} />
            {isVideoOff && <div className="w-full h-full bg-gray-800 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></div>}
          </div>
        </>
      )}

      {/* Header */}
      <header className="relative z-20 flex items-center justify-between p-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
        <div className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg><span className="text-xs text-white">端對端加密</span></div>
        {isVideoCall && <button onClick={switchCamera} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg></button>}
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-4">
        {!isVideoCall && (
          <div className="mb-8 relative">
            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-5xl text-white font-semibold">{recipientId?.charAt(0).toUpperCase()}</span>
            </div>
            {callState === 'connected' && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-secure-green rounded-full border-2 border-white" />}
          </div>
        )}
        <h1 className="text-2xl font-bold text-white mb-2">{recipientId}</h1>
        <p className={`text-lg ${callState === 'failed' ? 'text-red-300' : 'text-white/80'}`}>{getStatusText()}</p>
        <div className="mt-4 flex items-center gap-2 text-white/60">
          {isVideoCall ? <span>📹 視訊通話</span> : <span>📞 語音通話</span>}
          {isScreenSharing && <span className="text-green-400">• 螢幕分享中</span>}
        </div>
      </main>

      {/* Call controls */}
      <footer className="relative z-20 pb-12 pt-8">
        <div className="flex items-center justify-center gap-4">
          {/* Mute */}
          <button onClick={toggleMute} className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`}>
            {isMuted ? <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
             : <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>}
          </button>

          {/* Speaker (audio only) */}
          {!isVideoCall && (
            <button onClick={() => setIsSpeaker(!isSpeaker)} className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isSpeaker ? 'bg-white text-gray-900' : 'bg-white/20 hover:bg-white/30'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${isSpeaker ? 'text-gray-900' : 'text-white'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
            </button>
          )}

          {/* End call */}
          <button onClick={handleEndCall} className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" /></svg>
          </button>

          {/* Video toggle */}
          {isVideoCall && (
            <button onClick={toggleVideo} className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isVideoOff ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />{isVideoOff && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />}</svg>
            </button>
          )}

          {/* Screen share */}
          {isVideoCall && (
            <button onClick={handleScreenShare} className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isScreenSharing ? 'bg-green-500' : 'bg-white/20 hover:bg-white/30'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </button>
          )}
        </div>
      </footer>
    </div>
  )
}
